import { createReducer, createAction, createSelector } from 'redux-starter-kit';
import { find, filter } from 'ramda';
import api from 'api/api';

import { ProjectType } from 'store/modules/projects/projects.types';
import { UserType } from 'store/modules/auth/auth';
import { BusinessSectorType } from 'store/modules/assistant/assistant';

// State
export interface InvitationType {
  id: number;
  used: boolean;
  sent: boolean;
  email: string;
  key: string;
}

export interface OrganizationUsersType {
  count: number;
  next: string | null;
  previous: string | null;
  list: UserType[];
}

export interface OrganizationType {
  available_tokens: number;
  total_spent_tokens: number;
  additional_address: string | null;
  address: string | null;
  city: string | null;
  codename: string;
  country: string | null;
  currency: string | null;
  id: number;
  name: string;
  logo: string | null;
  stats: {
    projects: number;
    drafts: number;
  };
  drafts: number;
  projects: number;
  type: number;
  organization_role: number;
  business_sectors: BusinessSectorType[];
  business_sectors_ids?: number[];
}

export interface TemplateType {
  id: number;
  name: string | null;
  uuid: string | null;
  json: string | null;
  project_uuid: string | null;
  active: boolean | null;
}

export interface OrganizationProjectsType {
  count: number;
  next: string | null;
  previous: string | null;
  list: ProjectType[];
}

export interface OrgnizationTemplatesType {
  count: number;
  next: string | null;
  previous: string | null;
  list: TemplateType[];
}

export interface OrgnizationInvitationsType {
  count: number;
  next: string | null;
  previous: string | null;
  list: InvitationType[];
}

export interface OperationType {
  type: string;
  date: string;
  amount: number;
  saldo: number;
}

export interface OrganizationCreditsType {
  balance: number;
  spent: number;
  creditsHistory: OperationType[];
}

export interface OrganizationsState {
  count: number;
  next: string | null;
  previous: string | null;
  list: OrganizationType[];
  projects: OrganizationProjectsType;
  templates: OrgnizationTemplatesType;
  users: OrganizationUsersType;
  invitations: OrgnizationInvitationsType;
  credits: OrganizationCreditsType;
}

export const initialState: OrganizationsState = {
  count: 0,
  next: null,
  previous: null,
  list: [],
  projects: {
    count: 0,
    next: null,
    previous: null,
    list: [],
  },
  templates: {
    count: 0,
    next: null,
    previous: null,
    list: [],
  },
  users: {
    count: 0,
    next: null,
    previous: null,
    list: [],
  },
  invitations: {
    count: 0,
    next: null,
    previous: null,
    list: [],
  },
  credits: {
    balance: 0,
    spent: 0,
    creditsHistory: [],
  },
};

// Actions
const SET_ORGANIZATIONS = 'assistant/SET_ORGANIZATIONS';
const SET_ORGANIZATION_PROJECTS = 'assistant/SET_ORGANIZATION_PROJECTS';
const SET_TEMPLATES = 'assistant/SET_ORGANIZATION_TEMPLATES';
const SET_ORGANIZATION_PROJECTS_LIST =
  'assistant/SET_ORGANIZATION_PROJECTS_LIST';
const SET_ORGANIZATIONS_USERS = 'assistant/SET_ORGANIZATIONS_USERS';
const SET_ORGANIZATIONS_INVITATIONS = 'assistant/SET_ORGANIZATIONS_INVITATIONS';
const SET_ORGANIZATIONS_USER = 'assistant/SET_ORGANIZATIONS_USER';
const SET_ORGANIZATION_CREDITS = 'assistant/SET_ORGANIZATION_CREDITS';

const setOrganizations = createAction(SET_ORGANIZATIONS);
const setOrganizationProjects = createAction(SET_ORGANIZATION_PROJECTS);
const setTemplates = createAction(SET_TEMPLATES);
const setOrganizationProjectsList = createAction(
  SET_ORGANIZATION_PROJECTS_LIST
);
const setOrganizationsUsers = createAction(SET_ORGANIZATIONS_USERS);
const setOrganizationsInvitations = createAction(SET_ORGANIZATIONS_INVITATIONS);
const setOrganizationsUser = createAction(SET_ORGANIZATIONS_USER);
const setOrganizationCredits = createAction(SET_ORGANIZATION_CREDITS);

export const organizationsActions = {
  setOrganizations,
  setOrganizationProjects,
  setTemplates,
  setOrganizationProjectsList,
  setOrganizationsUsers,
  setOrganizationsInvitations,
  setOrganizationsUser,
  setOrganizationCredits,
  refreshProjects: ({
    project,
    organization_name,
  }: {
    project: ProjectType;
    organization_name: string;
  }) => (dispatch: any, getState: any) => {
    const projects = getState().organizations.projects.list;
    api.resources
      .getOrganizationProject({
        organization_name,
        id: project.id,
      })
      .then(({ data }) => {
        const newProjectList = filter(
          (data: ProjectType) => data.id !== project.id,
          projects
        );

        dispatch(setOrganizationProjectsList([...newProjectList, data]));
      });
  },
};

// Selectors
export const organizationsSelectors = {
  organizations: createSelector(['organizations']),
  organizationById: (id: string) =>
    createSelector(['organizations.list'], (organizations) =>
      find(
        (organization: OrganizationType) => organization.id === parseInt(id, 10)
      )(organizations)
    ),
  projects: createSelector(['organizations.projects']),
  projectById: (projectId: string) =>
    createSelector(['organizations.projects.list'], (projects) =>
      find((project: ProjectType) => project.id === parseInt(projectId, 10))(
        projects
      )
    ),
  templates: createSelector(['organizations.templates']),
  users: createSelector(['organizations.users']),
  invitations: createSelector(['organizations.invitations']),
  credits: createSelector(['organization.credits']),
  userByUuid: (uuid: string) =>
    createSelector(['organizations.users.list'], (users) =>
      find((user: UserType) => user.uuid === uuid)(users)
    ),
};

// Reducers
export default createReducer(initialState, {
  [SET_ORGANIZATIONS]: (state, action) => {
    state.count = action.payload.count;
    state.next = action.payload.next;
    state.previous = action.payload.previous;
    state.list = action.payload.results;
  },
  [SET_ORGANIZATION_PROJECTS]: (state, action) => {
    state.projects.count = action.payload.count;
    state.projects.next = action.payload.next;
    state.projects.previous = action.payload.previous;
    state.projects.list = action.payload.results;
  },
  [SET_TEMPLATES]: (state, action) => {
    state.templates.count = action.payload.count;
    state.templates.next = action.payload.next;
    state.templates.previous = action.payload.previous;
    state.templates.list = action.payload.results;
  },
  [SET_ORGANIZATION_PROJECTS_LIST]: (state, action) => {
    state.projects.list = action.payload;
  },
  [SET_ORGANIZATIONS_USERS]: (state, action) => {
    state.users.count = action.payload.count;
    state.users.next = action.payload.next;
    state.users.previous = action.payload.previous;
    state.users.list = action.payload.results;
  },
  [SET_ORGANIZATIONS_INVITATIONS]: (state, action) => {
    state.invitations.count = action.payload.count;
    state.invitations.next = action.payload.next;
    state.invitations.previous = action.payload.previous;
    state.invitations.list = action.payload.results;
  },
  [SET_ORGANIZATIONS_USER]: (state, action) => {
    state.users.list = [action.payload];
  },
  //change
  [SET_ORGANIZATION_CREDITS]: (state, action) => {
    state.credits.balance += action.payload.amount;
    state.credits.spent = action.payload.amount;
    state.credits.creditsHistory = [
      ...state.credits.creditsHistory,
      action.payload.operation,
    ];
  },
});
